import { z } from 'zod';

import type { RunProductsParams } from '../services/curation.service';

import {
  CHANGE_SIZES,
  type ChangeSize,
  isReviewReason,
  REVIEW_SORTS,
  type ReviewReason,
  type ReviewSort,
  SCORE_BANDS,
  type ScoreBand,
} from './enrichment-review';
import { ITEM_STATUSES, PRODUCT_DISPOSITIONS, RUN_LINEAGE_STAGES } from './lineage-images-search';

/** One product's listings the Listings view narrows to (a product rarely has more than a few). */
export const MAX_LISTING_IDS = 50;
/** Spot-check ids carried in the URL (the decision panel sends 5). */
const MAX_SPOT_IDS = 50;

/** Sort choices the run-products table offers (the server sorts by title | brand | category | sku). */
export const RUN_PRODUCTS_SORTS = ['title-asc', 'title-desc', 'brand', 'category', 'sku'] as const;

/** Page sizes offered on the run-products tables (server max is 100). */
export const RUN_PRODUCTS_PAGE_SIZES = [20, 50, 100];

/** Rows per page when the URL does not say. */
export const DEFAULT_PAGE_SIZE = 20;

/** Score-after bands the Products filter offers: below 70, 70–89, 90 and up. */
export const BATCH_SCORE_BANDS = ['below70', '70to89', '90plus'] as const;
/** Review reasons content generation can filter on exactly (B10). */
export const BATCH_FLAG_REASONS = [
  'SCORE_DROPPED',
  'SCORER_FLAGGED',
  'TITLE_TOO_LONG',
  'DESCRIPTION_TOO_SHORT',
  'DESCRIPTION_TOO_LONG',
] as const;
/** Server orders on the enriched list: score after or its change, either direction. */
export const BATCH_PRODUCT_ORDERS = ['postDesc', 'postAsc', 'deltaDesc', 'deltaAsc'] as const;

/**
 * URL search params of /catalog-enrichment/runs/$runId/products, so filters, sort, page and
 * the active tab survive Back. Every field is optional (links need not pass any) and
 * falls back to its default on a bad value.
 */
export const runProductsSearchSchema = z.object({
  // `listings` is the per-listing × stage audit grid folded in from the former Lineage tab.
  view: z.enum(['products', 'skipped', 'listings']).optional().catch(undefined),
  q: z.string().optional().catch(undefined),
  /** The Products tab's step + outcome filter (`CURATION:FAILED`, `DELIVERY:PASSED`). */
  filter: z
    .string()
    .regex(/^[A-Z_]+:[A-Z_]+$/)
    .optional()
    .catch(undefined),
  attention: z.enum(['all', 'flagged', 'clean']).optional().catch(undefined),
  sort: z.enum(RUN_PRODUCTS_SORTS).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  size: z.number().int().min(1).max(100).optional().catch(undefined),
  skippedPage: z.number().int().min(1).optional().catch(undefined),
  // The Listings view's own filters (ex-Lineage tab URL state). INSIGHTS isn't a lineage-ledger
  // stage (the manual Stage ▾ picker never offers it) but the Overview's INSIGHTS row drills in
  // with it per the approved design, so it's accepted as a deep-link value alongside the rest.
  stage: z
    .union([z.enum(RUN_LINEAGE_STAGES), z.literal('INSIGHTS')])
    .optional()
    .catch(undefined),
  status: z.enum(ITEM_STATUSES).optional().catch(undefined),
  disposition: z.enum(PRODUCT_DISPOSITIONS).optional().catch(undefined),
  /** Opens one listing's preview in the Listings view — the product page's "Open in batch ↗". */
  listing: z.string().optional().catch(undefined),
  /** Opens the journey drawer for this feed product on arrival (any view). */
  feedProductId: z.string().optional().catch(undefined),
  /** The Listings view narrowed to one product's listings — a product row's "N listings" badge. */
  listingIds: z.array(z.string()).max(MAX_LISTING_IDS).optional().catch(undefined),
  /** Design B — "⚑ Needs your review": the Content generation review list (the panel's drills). */
  lens: z.enum(['review']).optional().catch(undefined),
  /** Lens filters, each a removable chip: a field key, a review reason, a server tier, a type. */
  field: z.string().optional().catch(undefined),
  reason: z
    .custom<ReviewReason | 'any'>((v) => v === 'any' || isReviewReason(v))
    .optional()
    .catch(undefined),
  // NEEDS_LOOK = REVIEW or FAIL — the "⚑ Needs your review" segment's own rule (the verdict's).
  tier: z.enum(['PASS', 'REVIEW', 'FAIL', 'NEEDS_LOOK']).optional().catch(undefined),
  type: z.string().optional().catch(undefined),
  /** The decision panel's "Spot-check N" — the seeded sample of products that look fine. */
  spot: z.array(z.string()).max(MAX_SPOT_IDS).optional().catch(undefined),
  /** Lens focus mode on one product — the old `/gate?productId=` deep link lands here. */
  focus: z.string().optional().catch(undefined),
  /** Option 2 — the gate the decision panel shows (a stage code); absent = the open gate. */
  decision: z.string().min(1).optional().catch(undefined),
  /** Option 2 — the decision panel's state; absent = expanded (collapsed on small windows). */
  panel: z.enum(['collapsed', 'expanded']).optional().catch(undefined),
  /** Review toolbar (all URL-held, so a shared review link opens the same queue): search text,
   *  score band, change size, brand, market, sort and family grouping. */
  rq: z.string().optional().catch(undefined),
  score: z
    .custom<ScoreBand>((v) => SCORE_BANDS.includes(v as ScoreBand))
    .optional()
    .catch(undefined),
  change: z
    .custom<ChangeSize>((v) => CHANGE_SIZES.includes(v as ChangeSize))
    .optional()
    .catch(undefined),
  brand: z.string().optional().catch(undefined),
  market: z.string().optional().catch(undefined),
  rsort: z
    .custom<ReviewSort>((v) => REVIEW_SORTS.includes(v as ReviewSort))
    .optional()
    .catch(undefined),
  group: z.enum(['family']).optional().catch(undefined),
  /** The product whose field sheet (narrow screens) or score breakdown is open. */
  sheet: z.string().optional().catch(undefined),
  scores: z.string().optional().catch(undefined),
  /** Focus mode's (and the Products split preview's) field search. */
  fq: z.string().optional().catch(undefined),
  /** Products tab — the product open in the split preview. */
  product: z.string().optional().catch(undefined),
  /** Products tab content filters (content generation's enriched list): tier, score-after band,
   *  review reason, an attribute it generated, and the order. Named apart from the review lens's
   *  `tier` / `reason` / `field` so an old-link redirect never mistakes them for a review. */
  verdict: z.enum(['PASS', 'REVIEW', 'FAIL']).optional().catch(undefined),
  band: z.enum(BATCH_SCORE_BANDS).optional().catch(undefined),
  flag: z.enum(BATCH_FLAG_REASONS).optional().catch(undefined),
  attr: z.string().max(120).optional().catch(undefined),
  order: z.enum(BATCH_PRODUCT_ORDERS).optional().catch(undefined),
  /** The decision panel's own list (pre-score / sample bodies): its page, tier chip, the
   *  "below threshold" / "failed only" drills, the open score breakdown and curation's outcome
   *  sheet. Cleared whenever another decision is picked (`GATE_VIEW_CLEAR`). */
  gpage: z.number().int().min(1).optional().catch(undefined),
  gtier: z.string().optional().catch(undefined),
  gbelow: z.boolean().optional().catch(undefined),
  gfailed: z.boolean().optional().catch(undefined),
  gscores: z.string().optional().catch(undefined),
  outcome: z.string().optional().catch(undefined),
});

/** Clears the decision panel's own list state (see `gpage`…`outcome`). */
export const GATE_VIEW_CLEAR = {
  gpage: undefined,
  gtier: undefined,
  gbelow: undefined,
  gfailed: undefined,
  gscores: undefined,
  outcome: undefined,
} as const;

/** Review-route search updater: pushes a history entry unless `replace` (search keystrokes). */
export type RunProductsSearchChange = (
  patch: Partial<RunProductsSearch>,
  opts?: { replace?: boolean },
) => void;

/** Rows per page on the Products tab's product table when the URL does not say. */
export const BATCH_PRODUCTS_DEFAULT_SIZE = 25;

/**
 * `/runs/$runId/products` — one row per product: the step + outcome tab, search and page. Every
 * view of the tab is its own route with its own params, so a shared URL always opens the same view.
 */
export const batchProductsSearchSchema = z.object({
  filter: runProductsSearchSchema.shape.filter,
  q: runProductsSearchSchema.shape.q,
  page: runProductsSearchSchema.shape.page,
  size: runProductsSearchSchema.shape.size,
  /** The product open in the split preview (catalog product id). */
  product: runProductsSearchSchema.shape.product,
  /** The preview's field search. */
  fq: runProductsSearchSchema.shape.fq,
  /** The preview's score breakdown sheet (catalog product id). */
  scores: runProductsSearchSchema.shape.scores,
  verdict: runProductsSearchSchema.shape.verdict,
  band: runProductsSearchSchema.shape.band,
  flag: runProductsSearchSchema.shape.flag,
  attr: runProductsSearchSchema.shape.attr,
  order: runProductsSearchSchema.shape.order,
});

/** True when a content filter or order is on — content generation's list then drives the table. */
export function hasContentFilters(search: BatchProductsSearch): boolean {
  return !!(search.verdict ?? search.band ?? search.flag ?? search.attr ?? search.order);
}

/** Parsed `/products` search params. */
export type BatchProductsSearch = z.infer<typeof batchProductsSearchSchema>;

/** Keyset cursors the Listings URL carries (one per page walked past page 1). */
const MAX_CURSORS = 200;

/**
 * `/runs/$runId/products/listings` — one row per listing × step. Filters, the paged walk
 * (`cursors`, so Prev / Back / a shared link land on the same page), the page size, the opened
 * row's preview (`preview`), one listing (`listing`) and a pasted-id lookup (`listingIds`) all
 * live in the URL.
 */
export const listingsSearchSchema = z.object({
  stage: runProductsSearchSchema.shape.stage,
  status: runProductsSearchSchema.shape.status,
  disposition: runProductsSearchSchema.shape.disposition,
  listing: runProductsSearchSchema.shape.listing,
  listingIds: runProductsSearchSchema.shape.listingIds,
  /** The row whose preview is open (its grid key). */
  preview: z.string().optional().catch(undefined),
  cursors: z.array(z.string()).max(MAX_CURSORS).optional().catch(undefined),
  size: z.number().int().min(1).max(100).optional().catch(undefined),
});

/** Parsed `/products/listings` search params. */
export type ListingsSearch = z.infer<typeof listingsSearchSchema>;

/** `/runs/$runId/products/skipped` — the already-curated products a selection left out. */
export const skippedSearchSchema = z.object({
  page: runProductsSearchSchema.shape.page,
});

/** Parsed `/products/skipped` search params. */
export type SkippedSearch = z.infer<typeof skippedSearchSchema>;

/** Review-lens keys an old Products link may carry; any of them means the link was a review. */
const REVIEW_KEYS = [
  'lens',
  'decision',
  'field',
  'reason',
  'tier',
  'type',
  'spot',
  'focus',
  'attention',
  'panel',
] as const;

/** Old step drills (`?stage=&status=`) as the product table's outcome tab. */
const STATUS_TO_OUTCOME: Partial<Record<string, string>> = {
  FAILED: 'FAILED',
  SKIPPED: 'DROPPED',
  REVISIT: 'HELD_BACK',
  PENDING: 'IN_FLIGHT',
};

/** Where an old one-route Products URL lives now. */
export type LegacyProductsTarget =
  | { to: 'review'; search: Partial<RunProductsSearch> }
  | { to: 'listings'; search: ListingsSearch }
  | { to: 'skipped'; search: SkippedSearch }
  | { to: 'products'; search: BatchProductsSearch };

/**
 * Maps a pre-split Products URL (`?view=`, `?lens=`, `?decision=`, `?feedProductId=`, step
 * drills…) to the route that owns it now, or `null` when it is already a plain product-table URL.
 * Route `beforeLoad` redirects with it, so old bookmarks and Slack links keep working.
 */
export function legacyProductsTarget(search: RunProductsSearch): LegacyProductsTarget | null {
  if (REVIEW_KEYS.some((key) => search[key] !== undefined)) {
    const review: Partial<RunProductsSearch> = {};
    for (const key of [...REVIEW_KEYS, 'q', 'page', 'size', 'sort'] as const) {
      if (search[key] !== undefined) Object.assign(review, { [key]: search[key] });
    }
    return { to: 'review', search: review };
  }
  if (search.view === 'skipped') {
    return { to: 'skipped', search: { page: search.skippedPage } };
  }
  const drillOutcome = search.status ? STATUS_TO_OUTCOME[search.status] : undefined;
  if (search.stage && drillOutcome && !search.view) {
    return { to: 'products', search: { filter: `${search.stage}:${drillOutcome}` } };
  }
  if (
    search.view === 'listings' ||
    search.listing ||
    search.listingIds ||
    search.feedProductId ||
    search.stage ||
    search.status ||
    search.disposition
  ) {
    return {
      to: 'listings',
      search: {
        stage: search.stage,
        status: search.status,
        disposition: search.disposition,
        listing: search.listing ?? search.feedProductId,
        listingIds: search.listingIds,
      },
    };
  }
  if (search.view === 'products' || search.skippedPage !== undefined || search.sort) {
    return {
      to: 'products',
      search: { filter: search.filter, q: search.q, page: search.page, size: search.size },
    };
  }
  return null;
}

/** The lens's URL-held filters (the rest of the review toolbar stays page state). */
export type ReviewLensSearch = Pick<
  RunProductsSearch,
  'field' | 'reason' | 'tier' | 'type' | 'spot'
>;

/** Lens search for one drill — every other lens filter cleared, so a drill never stacks. */
export function reviewLensSearch(patch: ReviewLensSearch = {}): Partial<RunProductsSearch> {
  return {
    lens: 'review',
    field: patch.field,
    reason: patch.reason,
    tier: patch.tier,
    type: patch.type,
    spot: patch.spot,
    focus: undefined,
    attention: undefined,
    page: undefined,
    view: undefined,
    rq: undefined,
    score: undefined,
    change: undefined,
    brand: undefined,
    market: undefined,
    rsort: undefined,
    sheet: undefined,
    scores: undefined,
    fq: undefined,
  };
}

/** Parsed run-products search params. */
export type RunProductsSearch = z.infer<typeof runProductsSearchSchema>;

/**
 * URL search → GET curation-runs/{runId}/products params. The page's summary and the
 * table both build params here, so they share one query cache entry. Membership is the server's
 * default `source=auto`: an old run lists rows a later run took over (`still_current: false`).
 */
export function toRunProductsParams(search: RunProductsSearch): RunProductsParams {
  const attention = search.attention ?? 'all';
  return {
    page: search.page ?? 1,
    size: search.size ?? DEFAULT_PAGE_SIZE,
    // The table renders list-row keys only (§50; ignored by older backends).
    fields: 'summary',
    ...(search.q ? { search: search.q } : {}),
    ...(attention === 'all' ? {} : { needsAttention: attention === 'flagged' }),
    ...toSortParams(search.sort),
  };
}

/** Sort choice → the server's `sortBy` / `sortDir` pair. */
function toSortParams(
  sort: RunProductsSearch['sort'],
): Pick<RunProductsParams, 'sortBy' | 'sortDir'> {
  switch (sort) {
    case 'title-asc':
      return { sortBy: 'title', sortDir: 'asc' };
    case 'title-desc':
      return { sortBy: 'title', sortDir: 'desc' };
    case 'brand':
    case 'category':
    case 'sku':
      return { sortBy: sort, sortDir: 'asc' };
    default:
      return {};
  }
}
